import React, { useRef } from 'react';
import { useDebounceFn } from 'ahooks';
import { Form, Input, Checkbox, Button, Modal } from 'antd';
import { useRsaKey, useAntModal, useNECaptcha, get, specialRbacInnerCodeMap, checkIsBlank, checkEmail, REGEX_CROP_EMAIL, ZH, API, setUserAccountToken, ACTIVITY_CENTER_FROM_LOGIN_FLAG } from '@yaotai/common-utils';
import { EMAIL_LOGIN_LOCALE } from './helper.js';
import { EmailLoginLayout, ModalContentLayout } from './styles.js';

const loginByEmail = async ({ info, rsaKey, serverCode, cookieDomain, postServerLoginApi, encryptPassword, }) => {
    const submitInfo = { ...info, server: serverCode, password: encryptPassword(info?.password, rsaKey) };
    try {
        const result = await postServerLoginApi(submitInfo);
        if (!result?.data) {
            return Promise.reject();
        }
        const { RBAC_USER, RBAC_TOKEN } = result?.data || {};
        setUserAccountToken({ RBAC_USER: RBAC_USER, RBAC_TOKEN, cookieDomain });
        localStorage.setItem(ACTIVITY_CENTER_FROM_LOGIN_FLAG, 'true');
        return Promise.resolve(result);
    }
    catch (error) {
        return Promise.reject(error);
    }
};
const EmailLogin = ({ className, serverCode, cookieDomain, lang = ZH, mediaType = 'pc', invokeType = 'page', registerUrl, loginSuccessJumpUrl = window.location.href, handleNotRegistered, handlePasswordForgot, getRsaKeyApi = API.rbac.getRsaKey, postServerLoginApi = API.rbac.postServerLogin, forgetModalProps = {}, forgetModalClassName = '', handleForgetModalCustomerContact, extraLoginSuccessCb, encryptPassword, }) => {
    const [form] = Form.useForm();
    const { rsaKey } = useRsaKey({ getRsaKeyApi });
    const formRef = useRef({});
    const { showModal, commonAntModalProps, closeModal } = useAntModal();
    const { captchaIns, getCaptchaIns } = useNECaptcha({
        mode: 'bind',
        element: '#captcha_form_login',
        onVerify: function (err, data) {
            if (err)
                return;
            handleLogin(data.validate);
        },
    });
    const handleCaptcha = async () => {
        await form.validateFields();
        captchaIns?.verify();
    };
    const { run } = useDebounceFn(() => {
        handleCaptcha();
    }, {
        leading: true,
        trailing: false,
        wait: 300,
    });
    // eslint-disable-next-line react-func/max-lines-per-function
    const handleLogin = (valid) => {
        // eslint-disable-next-line react-func/max-lines-per-function
        form.validateFields().then(async (values) => {
            const info = {
                ...values,
                valid,
            };
            try {
                await loginByEmail({ info, rsaKey: rsaKey, serverCode, cookieDomain, postServerLoginApi, encryptPassword });
                await extraLoginSuccessCb?.();
                window.location.replace(loginSuccessJumpUrl);
            }
            catch (error) {
                const innerCode = get(error, 'response.data.code', 0);
                getCaptchaIns()?.refresh();
                if (innerCode === specialRbacInnerCodeMap.rbacThirdEmailNotRegister) {
                    formRef.current = { ...(formRef.current || {}), name: form.getFieldValue('name') };
                    form.validateFields(['name']);
                }
                else if (innerCode === specialRbacInnerCodeMap.rbacThirdEmailCodeError) {
                    formRef.current = { ...(formRef.current || {}), code: form.getFieldValue('password') };
                    form.validateFields(['password']);
                }
            }
        });
    };
    return (React.createElement(EmailLoginLayout, { className: className, mediaType: mediaType },
        React.createElement(Form, { form: form },
            React.createElement(Form.Item, { name: "name", rules: [
                    {
                        validator: checkIsBlank(EMAIL_LOGIN_LOCALE[lang]['emailNotNull']),
                    },
                    {
                        validator: checkEmail(false, EMAIL_LOGIN_LOCALE[lang]['incorrectEmail']),
                    },
                    {
                        validator: (rule, value) => {
                            if (REGEX_CROP_EMAIL.test(value)) {
                                return Promise.reject(EMAIL_LOGIN_LOCALE[lang]['noCorpEmail']);
                            }
                            return Promise.resolve();
                        },
                    },
                    {
                        validator: (rule, value) => {
                            if (value && value === formRef.current?.name) {
                                return Promise.reject(React.createElement(React.Fragment, null,
                                    EMAIL_LOGIN_LOCALE[lang]['notRegisteredNetEaseYaotai'],
                                    React.createElement("span", { style: { textDecoration: 'underline', cursor: 'pointer' }, onClick: handleNotRegistered }, EMAIL_LOGIN_LOCALE[lang]['goToRegister'])));
                            }
                            return Promise.resolve();
                        },
                    },
                ] },
                React.createElement(Input, { size: "large", allowClear: true, autoComplete: "off", placeholder: EMAIL_LOGIN_LOCALE[lang]['enterEmail'] })),
            React.createElement(Form.Item, { name: "password", rules: [
                    {
                        validator: checkIsBlank(EMAIL_LOGIN_LOCALE[lang]['passwordNotNull']),
                    },
                    {
                        validator: (rule, value) => {
                            if (value && value === formRef.current?.code) {
                                return Promise.reject(EMAIL_LOGIN_LOCALE[lang]['accountOrPasswordError']);
                            }
                            return Promise.resolve();
                        },
                    },
                ] },
                React.createElement(Input.Password, { size: "large", visibilityToggle: false, allowClear: true, autoComplete: "new-password", placeholder: EMAIL_LOGIN_LOCALE[lang]['password'] })),
            React.createElement(Form.Item, { name: "protocol", valuePropName: "checked", style: { marginBottom: '8px' }, rules: [
                    {
                        validator: (_, value) => {
                            if (!value) {
                                return Promise.reject(EMAIL_LOGIN_LOCALE[lang]['agreeTermsOfService']);
                            }
                            return Promise.resolve();
                        },
                    },
                ] },
                React.createElement("div", { className: "clause-tips" },
                    React.createElement("span", null,
                        React.createElement(Form.Item, { noStyle: true },
                            React.createElement(Checkbox, { style: { width: 20 } })),
                        React.createElement("span", { className: "tips-text" },
                            EMAIL_LOGIN_LOCALE[lang]['readAndAgree'],
                            React.createElement("a", { href: "https://hc.reg.163.com/iTerm/doc.html?id=542", target: "_blank", rel: "noreferrer" }, EMAIL_LOGIN_LOCALE[lang]['termsOfService']),
                            EMAIL_LOGIN_LOCALE[lang]['and'],
                            React.createElement("a", { href: "https://hc.reg.163.com/iTerm/doc.html?id=437", target: "_blank", rel: "noreferrer" }, EMAIL_LOGIN_LOCALE[lang]['netEasePrivacyPolicy']))))),
            React.createElement(Form.Item, null,
                React.createElement("div", { id: "captcha_form_login" }),
                React.createElement(Button, { type: "primary", className: "login-btn", onClick: run }, EMAIL_LOGIN_LOCALE[lang]['login']),
                React.createElement("div", { className: "more-tips" },
                    React.createElement("a", { onClick: () => {
                            handlePasswordForgot ? handlePasswordForgot() : showModal();
                        } }, EMAIL_LOGIN_LOCALE[lang]['forgotPassword']),
                    React.createElement("span", null,
                        EMAIL_LOGIN_LOCALE[lang]['noAccount'],
                        React.createElement("a", { href: registerUrl }, EMAIL_LOGIN_LOCALE[lang]['createAccount']))))),
        React.createElement(Modal, { ...commonAntModalProps, centered: true, width: 480, footer: null, ...forgetModalProps },
            React.createElement(ModalContentLayout, { mediaType: mediaType, className: forgetModalClassName },
                React.createElement("p", { className: "tips" }, EMAIL_LOGIN_LOCALE[lang]['contactCustomerService']),
                React.createElement("p", { className: "phone" }, "(0571)89852163 \u8F6C 21951"),
                React.createElement(Button, { type: "primary", className: "modal-btn", onClick: async () => {
                        await handleForgetModalCustomerContact?.();
                        closeModal();
                    } }, EMAIL_LOGIN_LOCALE[lang]['ContactCustomerService'])))));
};

export { EmailLogin };
